import { Colors } from "../../config/DesignConstants";
import Entity from "../../core/entity/Entity";
import { clampUp } from "../../core/util/MathUtil";
import {
  calculateOutwardDirection,
  calculatePolygonCenter,
  extractLineSegments,
} from "../../core/util/PolygonUtils";
import { rNormal, rUniform } from "../../core/util/Random";
import { V, V2d } from "../../core/Vector";
import { MovementFilter } from "../graphics";
import VectorSprite from "../graphics/VectorSprite";
import { createSegmentsFromExtracted } from "../graphics/VectorSpriteUtils";
import { LevelEntity } from "../LevelEntity";

const DEFAULT_LIFETIME = 2.0;
const DEFAULT_VELOCITY_SCALE = 50.0;
const PARTICLE_VELOCITY_MIN = 1;
const PARTICLE_VELOCITY_MAX = 2;
const PARTICLE_STROKE_WIDTH = 1;

interface BrokenExpandOptions {
  position: V2d;
  polygon: V2d[];
  rotation?: number; // Entity rotation in radians
  lifetime?: number;
  velocityScale?: number;
  inheritedVelocity?: V2d;
  color?: number;
  colors?: number[];
}

export default class BrokenExpandEffect extends LevelEntity implements Entity {
  private renderer: VectorSprite;
  lifetime: number = DEFAULT_LIFETIME;
  currentTime: number = 0;

  constructor({
    position,
    polygon,
    rotation = 0,
    lifetime = DEFAULT_LIFETIME,
    velocityScale = DEFAULT_VELOCITY_SCALE,
    inheritedVelocity,
    color = Colors.WHITE,
    colors,
  }: BrokenExpandOptions) {
    super();

    this.lifetime = lifetime;

    // Apply rotation to polygon points if specified
    const rotatedPolygon =
      rotation !== 0
        ? polygon.map((point) => V(point).rotate(rotation))
        : polygon;

    // Calculate the polygon center for proper outward movement
    const polygonCenter = calculatePolygonCenter(rotatedPolygon);

    // Extract line segments from the rotated polygon
    const segments = extractLineSegments(rotatedPolygon);

    // Use colors array if provided, otherwise use single color
    const particleColors = colors && colors.length > 0 ? colors : [color];

    // Create line segments with randomized colors
    const lineSegments = createSegmentsFromExtracted(
      segments,
      particleColors,
      PARTICLE_STROKE_WIDTH,
    );

    // Create the renderer
    this.renderer = new VectorSprite(lineSegments, "background");
    this.renderer.setPosition(position);

    // Calculate velocities for each segment
    const velocities: V2d[] = segments.map((segment) => {
      const baseVelocity =
        rUniform(PARTICLE_VELOCITY_MIN, PARTICLE_VELOCITY_MAX) * velocityScale;
      const outwardVelocity = calculateOutwardDirection(segment, polygonCenter)
        .imul(baseVelocity)
        .imul(rNormal(1, 0.5));

      // Add inherited velocity if provided
      return inheritedVelocity
        ? outwardVelocity.add(inheritedVelocity)
        : outwardVelocity;
    });

    // Create and configure filters
    const movementFilter = new MovementFilter(velocities);

    // Add filters to renderer
    this.renderer.addFilter(movementFilter);
  }

  onAdd() {
    // Add the renderer as a child entity
    this.addChild(this.renderer);
  }

  onTick(dt: number) {
    this.currentTime += dt;

    const progress = this.currentTime / this.lifetime;
    const alpha = clampUp(1 - progress); // Fade out over time

    // Update fade filter
    this.renderer.setAlpha(alpha);

    // Destroy when lifetime is over
    if (this.currentTime >= this.lifetime) {
      this.destroy();
    }
  }
}
